import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';

export interface MpvCultureMapping {
    productKey: string;
    culture: string;
}

export interface MpvLookupResult extends MpvCultureMapping {
    alternates: MpvCultureMapping[];
}

class MsxServiceApi extends AbstractService {
    /**
     * Resolves an MPV id (e.g. "notebooks") to a product key. Caller passes one or more
     * cultures (MSX requires at least one); the response includes only the cultures where
     * the MPV resolves. Picks the first culture from `cultures` that has a hit and returns
     * the rest as alternates so callers can offer a region switcher.
     *
     * MSX response shape: { "<culture>": "PRD-..." } — only cultures with a hit are present.
     */
    public async getProductKeyByMpvId(
        mpvId: string,
        cultures: string[],
        signal?: AbortSignal,
    ): Promise<MpvLookupResult | null> {
        const url = `api/${this.version}/tenant/vistaprint/mpvs/${encodeURIComponent(mpvId)}`;

        const searchParams = new URLSearchParams();

        searchParams.append('requestor', config.appName);
        cultures.forEach((c) => searchParams.append('cultures', c));

        try {
            const response = await this.api
                .get(url, { searchParams, signal })
                .json<Record<string, string>>();

            const entries = Object.entries(response).filter(([, prd]) => !!prd);

            if (entries.length === 0) return null;

            const orderedHit = cultures
                .map((c) => entries.find(([k]) => k.toLowerCase() === c.toLowerCase()))
                .find((entry) => !!entry)
                ?? entries[0];
            const alternates: MpvCultureMapping[] = entries
                .filter(([k]) => k !== orderedHit[0])
                .map(([k, prd]) => ({ culture: k, productKey: prd }));

            return { culture: orderedHit[0], productKey: orderedHit[1], alternates };
        } catch (e: unknown) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to resolve MPV ID to product key',
                info: (e as Error).message,
                url,
                mpvId,
                cultures: cultures.join(','),
            });
        }
    }
}

export const MsxService = new MsxServiceApi(config.services.msxServiceApi);
